/**
 * leasing-labels.ts
 *
 * Canonical human-readable display labels for the leasing/listing enums that the API stores
 * as snake_case canonical values (ADR-LS-007). Centralized here so every consumer — the landlord
 * editor, the public prospect listing page, and any future channel — renders the SAME label for a
 * given enum value instead of each app maintaining its own copy that silently drifts when a new
 * enum value ships server-side.
 *
 * Maps are string-keyed; callers use `labelFor(MAP, value)` which falls back to a prettified form
 * for any value not yet in the map (so a new server enum renders readably, never as raw snake_case).
 */
export const LEASE_TERM_LABELS = {
    month_to_month: 'Month-to-month',
    '6_months': '6 months',
    '12_months': '1 year',
    '24_months': '2 years',
};
export const UTILITY_LABELS = {
    water: 'Water',
    sewer: 'Sewer',
    trash: 'Trash',
    gas: 'Gas',
    electric: 'Electric',
    heat: 'Heat',
    cooling: 'Cooling',
    internet: 'Internet',
    cable: 'Cable',
};
export const FURNISHED_LABELS = {
    unfurnished: 'Unfurnished',
    partial: 'Partially furnished',
    furnished: 'Furnished',
};
/**
 * The unit marketing profile's property type, as shown on listings. Not the property's internal
 * single-family / multi-family flag, which describes how Resida manages the units and is never posted.
 */
export const PROPERTY_TYPE_LABELS = {
    apartment: 'Apartment',
    house: 'House',
    condo: 'Condo',
    townhouse: 'Townhouse',
    room: 'Room',
    studio: 'Studio',
};
export const PARKING_TYPE_LABELS = {
    none: 'None',
    street: 'Street parking',
    garage: 'Garage',
    covered: 'Covered',
    carport: 'Carport',
    driveway: 'Driveway',
};
export const LAUNDRY_TYPE_LABELS = {
    in_unit: 'In-unit',
    shared: 'Shared',
    hookups: 'Hookups only',
    washer_only: 'Washer only',
    none: 'None',
    other: 'Other',
};
export const HEATING_TYPE_LABELS = {
    forced_air: 'Forced air',
    heat_pump: 'Heat pump',
    baseboard: 'Baseboard',
    radiant: 'Radiant',
    radiator: 'Radiator',
    stove: 'Stove',
    wall: 'Wall',
    electric: 'Electric',
    none: 'None',
    other: 'Other',
};
export const COOLING_TYPE_LABELS = {
    central: 'Central air',
    window: 'Window unit',
    mini_split: 'Mini split',
    evaporative: 'Evaporative (swamp)',
    geothermal: 'Geothermal',
    wall: 'Wall',
    solar: 'Solar',
    none: 'None',
    other: 'Other',
};
export const AMENITY_LABELS = {
    pool: 'Pool',
    fitness_center: 'Fitness Center',
    elevator: 'Elevator',
    doorman: 'Doorman',
    rooftop: 'Rooftop Deck',
    storage: 'Storage',
    bike_storage: 'Bike Storage',
    balcony: 'Balcony',
    patio: 'Patio',
    yard: 'Yard',
    fireplace: 'Fireplace',
    dishwasher: 'Dishwasher',
    hardwood_floors: 'Hardwood Floors',
    high_ceilings: 'High Ceilings',
    ev_charging: 'EV Charging',
    washer: 'Washer',
    dryer: 'Dryer',
    refrigerator: 'Refrigerator',
    microwave: 'Microwave',
    garbage_disposal: 'Garbage Disposal',
    range_oven: 'Range / Oven',
    freezer: 'Freezer',
    large_closets: 'Large Closets',
    ceiling_fan: 'Ceiling Fan',
    window_coverings: 'Window Coverings',
    gated_entry: 'Gated Entry',
    controlled_access: 'Controlled Access',
    security_system: 'Security System',
    intercom: 'Intercom',
    package_receiving: 'Package Receiving',
    hot_tub_spa: 'Hot Tub / Spa',
    sauna: 'Sauna',
    barbecue_area: 'Barbecue Area',
    courtyard: 'Courtyard',
    tennis_court: 'Tennis Court',
    business_center: 'Business Center',
    concierge: 'Concierge',
};
export const PET_TYPE_LABELS = {
    dogs: 'Dogs',
    cats: 'Cats',
    birds: 'Birds',
    reptiles: 'Reptiles',
    fish: 'Fish',
    other: 'Other',
};
export const MAX_PET_SIZE_LABELS = {
    tiny: 'Tiny',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    huge: 'Huge',
    all: 'All sizes',
};
/** ListingScreeningCriteria.smokingPolicy (Prisma enum SmokingPolicy). */
export const SMOKING_POLICY_LABELS = {
    prohibited: 'Not permitted',
    outdoor_only: 'Outdoor only',
    permitted: 'Permitted',
};
/** Prettify an unknown enum value — capitalize words, remove underscores. */
export function prettifyEnum(value) {
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
/** Resolve a display label, falling back to a prettified form for unmapped values. */
export function labelFor(map, value) {
    return map[value] ?? prettifyEnum(value);
}
/** `{ value, label }` options for a vocabulary, in the vocabulary's order (form selects). */
export function labelledOptions(values, labels) {
    return values.map((value) => ({ value, label: labels[value] }));
}
//# sourceMappingURL=leasing-labels.js.map